import type { TippyProps } from '@tippyjs/react/headless';
import Tippy from '@tippyjs/react/headless';
import clsx from 'clsx';
import type { Placement } from 'tippy.js';

const arrowStyle = {
    top: {
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid currentColor'
    },
    bottom: {
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: '6px solid currentColor'
    },
    left: {
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderLeft: '6px solid currentColor'
    },
    right: {
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderRight: '6px solid currentColor'
    }
} as const;

const arrowVariant = {
    default: 'text-icoBlack dark:text-icoDarkMode-deepBlack',
    menu: 'text-white dark:text-icoDarkMode-deepBlack',
    tag: 'text-icoBlack/90',
    pricing_slider: 'text-icoBlue'
} as const;

type TooltipArrowProps = Pick<TooltipProps, 'position'> & {
    variant: Pick<TooltipProps, 'arrowVariant'>['arrowVariant'];
};

const Arrow = ({ position = 'bottom', variant }: TooltipArrowProps) => {
    const mainPosition = position.split('-')[0];
    const isTop = mainPosition === 'top';
    const isBottom = mainPosition === 'bottom';
    const isRight = mainPosition === 'right';
    const isLeft = mainPosition === 'left';

    return (
        <div
            data-testid="tooltip-arrow"
            style={arrowStyle[mainPosition]}
            className={clsx('relative h-1.5 w-3', arrowVariant[variant], {
                '-bottom-1.5': isTop,
                '-top-1.5': isBottom,
                '-right-3': isLeft,
                '-left-3': isRight
            })}
            data-popper-arrow={position}
        />
    );
};

const TooltipStyleAvatar =
    'rounded-m px-3 py-1 text-xs font-GSemiBold shadow-icoXL border border-white backdrop-blur-md bg-white/80 dark:bg-icoDarkMode-deepBlack/80 dark:border-icoDarkMode-deepBlack firefox:bg-white dark:firefox:bg-icoDarkMode-deepBlack dark:firefox:bg-opacity-95 firefox:bg-opacity-95';

const TooltipStyle = {
    default:
        'bg-icoBlack dark:bg-icoDarkMode-deepBlack rounded-md text-white text-center p-3 max-w-xs text-13 font-GMedium leading-[18px]',
    wrapper:
        'bg-white dark:bg-icoDarkMode-anthracite rounded-md border border-icoGray-300 dark:border-icoDarkMode-beton p-0.5',
    menu: 'bg-white dark:bg-icoDarkMode-deepBlack rounded-md text-icoBlack dark:text-white text-xxs tracking-1 uppercase px-1.5 py-1 font-GSemiBold shadow-icoXL leading-none',
    avatar: `text-icoBlue ${TooltipStyleAvatar}`,
    avatarError: `text-icoGray-500 dark:text-icoDarkMode-wolf ${TooltipStyleAvatar}`,
    tag: 'bg-icoBlack/90 rounded text-white p-2.5 text-xs',
    tagScheduler: 'bg-icoBlack rounded-md text-white text-xxs p-2',
    pricing_slider:
        'flex items-center justify-center bg-icoBlue rounded-xl text-white text-center w-10 h-[30px] text-sm',
    profile_tooltip: 'bg-white dark:bg-icoDarkMode-deepBlack rounded-m text-xxs p-1 shadow-icoXS',
    plan_feature:
        'bg-white p-5 rounded-md border border-icoGray-300 shadow-icoS text-sm max-w-[250px] dark:bg-icoDarkMode-deepBlack dark:border-icoDarkMode-beton'
} as const;

export type TooltipProps = TippyProps & {
    position?: Placement;
    arrow?: boolean;
    variant?: keyof typeof TooltipStyle;
    arrowVariant?: keyof typeof arrowVariant;
    tooltipAdditionalClasses?: string;
};

const Tooltip = ({
    children,
    content,
    position = 'bottom',
    arrow = true,
    variant = 'default',
    arrowVariant = 'default',
    tooltipAdditionalClasses,
    ...restProps
}: TooltipProps) => {
    return (
        <Tippy
            placement={position}
            render={(attrs) => {
                if (content) {
                    return (
                        <div
                            className={clsx(TooltipStyle[variant], tooltipAdditionalClasses)}
                            {...attrs}>
                            {content}
                            {arrow ? (
                                <Arrow variant={arrowVariant} position={attrs['data-placement']} />
                            ) : null}
                        </div>
                    );
                }

                return null;
            }}
            {...restProps}>
            {children}
        </Tippy>
    );
};

export default Tooltip;
